import { useEffect, useState } from "react";
import { Sparkles, X } from "lucide-react";
import { Button } from "../../../components/ui/button";
import { useOnboardingState, useCompleteOnboarding } from "@/lib/notificationQueries";

/**
 * Master v5 Prompt 13 — FeatureOnboardingState: appears the FIRST time a
 * feature is used, supports Skip, and can be reopened from Help (see
 * `ClientShell.jsx`'s Help dialog "Replay a tour" section, which calls
 * the reset endpoint this same `useOnboardingState` query re-reads on
 * this component's next mount). Never a generic forced tour — one
 * component instance is scoped to exactly one `featureKey`.
 */
export default function FeatureOnboarding({ workspaceId, featureKey, title, body }) {
  const { data: state } = useOnboardingState(workspaceId, featureKey);
  const complete = useCompleteOnboarding(workspaceId, featureKey);
  const [dismissedLocally, setDismissedLocally] = useState(false);

  useEffect(() => { setDismissedLocally(false); }, [state?.seen]);

  if (!state || state.seen || dismissedLocally) return null;

  function finish(skipped) {
    setDismissedLocally(true);
    complete.mutate(skipped);
  }

  return (
    <div
      className="mb-4 rounded-lg border border-terracotta/30 bg-terracotta/5 p-4 flex items-start gap-3"
      data-testid={`feature-onboarding-${featureKey}`}
    >
      <Sparkles size={18} className="text-terracotta shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-ink">{title}</p>
        <p className="text-sm text-ink-soft mt-0.5">{body}</p>
        <div className="flex gap-2 mt-3">
          <Button size="sm" onClick={() => finish(false)} data-testid={`feature-onboarding-${featureKey}-got-it-btn`}>
            Got it
          </Button>
          <Button size="sm" variant="ghost" onClick={() => finish(true)} data-testid={`feature-onboarding-${featureKey}-skip-btn`}>
            Skip
          </Button>
        </div>
      </div>
      <button
        onClick={() => finish(true)}
        className="text-ink-soft hover:text-ink shrink-0"
        aria-label="Dismiss"
        data-testid={`feature-onboarding-${featureKey}-close-btn`}
      >
        <X size={16} />
      </button>
    </div>
  );
}
